import React, { useEffect, useState } from 'react';

// In-memory cache of fetched SVG text (keyed by file path) and of finished
// data URIs (keyed by `${path}|${tint}`) so each award file is fetched once
// no matter how many tinted copies render.
const svgTextCache = new Map();
const tintedUriCache = new Map();

// The silver outer starburst present in every *Award.svg
// (verified: exactly one `#d0d2d3` fill per file).
const AWARD_BORDER_FILL = /#d0d2d3/gi;

async function loadTintedUri(src, tintBorder) {
  const cacheKey = `${src}|${tintBorder}`;
  const cached = tintedUriCache.get(cacheKey);
  if (cached) return cached;

  let svgText = svgTextCache.get(src);
  if (!svgText) {
    const response = await fetch(src);
    if (!response.ok) {
      throw new Error(`Badge SVG fetch failed: ${response.status}`);
    }
    svgText = await response.text();
    svgTextCache.set(src, svgText);
  }

  const tinted = svgText.replace(AWARD_BORDER_FILL, tintBorder);
  const uri = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(tinted)}`;
  tintedUriCache.set(cacheKey, uri);
  return uri;
}

/**
 * Badge artwork renderer.
 *
 * Plain tiers/locks render as a normal <img>. For the Award artwork, pass
 * `tintBorder` (a tier color): the file's silver outer ring is recolored
 * live and the tinted copy renders once ready (plain art shows meanwhile,
 * so there is no layout shift and no failure mode if the fetch fails).
 */
function BadgeImage({ src, alt = '', className, tintBorder = null }) {
  const [tintedSrc, setTintedSrc] = useState(null);

  useEffect(() => {
    if (!tintBorder) {
      setTintedSrc(null);
      return;
    }
    let cancelled = false;
    setTintedSrc(null);
    loadTintedUri(src, tintBorder).then(
      (uri) => {
        if (!cancelled) setTintedSrc(uri);
      },
      () => {
        // Keep the plain artwork on any failure.
        if (!cancelled) setTintedSrc(null);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [src, tintBorder]);

  return <img src={tintedSrc || src} alt={alt} className={className} />;
}

export default BadgeImage;
